// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import { extent, max, min } from 'd3-array';
import Zion from './utils/Zion';
import { linearAxis, timeAxis } from '../../../factory/axes';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;

const margins = {
  top: 20,
  right: 90,
  bottom: 25,
  left: 35,
};

const parser = d3.timeParse('%Y-%m-%d'); // https://github.com/d3/d3-time-format;

// let recessionData;
let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(CNNPolitics, CNNBusiness);

const rParser = d3.timeParse('%Y-%m-%d');

const recessionData = [
  {
    start: '2020-03-01', end: '2020-04-01', label: 'Covid-19', labelYOffset: -13, labelXOffset: 0,
  },
  {
    start: '2020-03-01', end: '2020-04-01', label: 'pandemic', labelYOffset: 0, labelXOffset: 0,
  },
  {
    start: '2007-12-01', end: '2009-06-01', label: 'Great Recession', labelYOffset: 0, labelXOffset: 0,
  },
  // { start: '2001-03-01', end: '2001-11-01', label: 'Early 2000s Recession' },
  // { start: '1990-07-01', end: '1991-03-01', label: 'Early 1990s Recession' },
];


const getDek = (cur, prev) => {
  if (!cur || !prev) return null;
  const date_str = d3.timeFormat('%B %Y')(cur.date);
  const rateDir = cur.value - prev.value;
  return [
    `The unemployment rate in the US in ${date_str} was ${cur.value}%,`,
    `${(rateDir < 0) ? 'falling from' : (rateDir > 0) ? 'rising from' : 'remaining the same as'}`,
    `${(rateDir === 0) ? 'the previous month.' : `the previous month’s rate of ${prev.value}%.`}`,
  ].join(' ');
};


d3.json('https://ix.cnn.io/data/unemployment/unemployment_rate_latest.json').then((data) => {
  rawData = data.map(d => ({
    date: d3.timeParse('%Y-%m-%d')(d.date),
    value: +d.value,
  }));

  chartData = rawData.filter(d => d.date > pTime('2000-01-01'));

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max(chartData, d => d.date);

  minValue = 0;
  maxValue = d3.max(chartData, d => d.value);

  lastData = chartData.filter(d => d.date == maxDomain)[0];

  // Dynamically build dek based on most recent data
  const dek = getDek(chartData[chartData.length - 1], chartData[chartData.length - 2]);

  if (dek && dek !== '') {
    d3.select('h2.dek')
      .text(dek);
  }

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 2 : 5;
  const yTicks = isMobile ? 5 : 5;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(xTicks)
    .tickFormat((d, i) => {
      if (i === 0 || d3.timeFormat('%m')(d) == '01') {
        return d3.timeFormat('%b %Y')(d);
      }
      return d3.timeFormat('%b')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat((d, i, x) => (i == x.length - 1 ? `${d3.format('.0f')(d)}%` : `${d3.format('.0f')(d)}`));

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  // Append rectangles and labels for each recession
  recessionData.forEach((rec) => {
    const startDate = new Date(rec.start);
    const endDate = new Date(rec.end);
    const labelXOffset = rec.labelXOffset || 0;
    const labelYOffset = rec.labelYOffset || 0;

    g.append('rect')
      .attr('x', xScale(startDate))
      .attr('width', xScale(endDate) - xScale(startDate))
      .attr('y', 0)
      .attr('height', chartHeight)
      .attr('fill', '#e3e3e3')
      .attr('opacity', 0.5);

    // Adjust label placement based on optional labelXOffset and labelYOffset
    g.append('text')
      .attr('x', ((xScale(startDate) + xScale(endDate)) / 2) + labelXOffset) // Use labelXOffset here
      .attr('y', chartHeight - 10 + labelYOffset) // Use labelYOffset here
      .attr('text-anchor', 'middle')
      .attr('class', 'label-recession')
      .text(rec.label);
  });

  const line = g.append('g')
    .attr('class', 'line');

  line.append('path')
    .datum(chartData)
    .attr('stroke', d => '#7c4ea5')
    .attr('stroke-width', '2')
    .attr('fill', 'none')
    .attr('d', path);

  d3.select('.grid.y .domain').remove();

  const annotation = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${[xScale(lastData.date), yScale(lastData.value)]})`);

  annotation.append('circle')
    .attr('r', '3')
    .attr('fill', '#7c4ea5')
    .attr('stroke', 'none');

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text(d3.timeFormat('%b %Y')(lastData.date));

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value')
    .text(`${d3.format('.1f')(lastData.value)}%`)
    .attr('dy', '1.2em');

  // Peak covid anno

  // const maxValData = chartData.filter(d => d.value === maxValue).pop();

  // g.append('text')
  //   .attr('x', chartWidth)
  //   .attr('y', yScale(maxValData.value))
  //   .attr('text-anchor', 'end')
  //   .attr('class', 'annotation-date')
  //   .attr('dy', '-0.5em')
  //   .attr('dx', '-2.5em')
  //   .text(`Covid-19 peak: ${maxValData.value}%`);

  // Append the current date as a timestamp below the chart
  const currentDate = new Date();
  const dateFormat = d3.timeFormat('%B %-d, %Y');
  const currentDateString = `Note: The seasonally adjusted figures are released monthly but lag for processing and review and are current as of ${dateFormat(currentDate)}. The latest value is preliminary.`;

  const timestamp = container.append('div')
    .attr('class', 'footnote')
    .text(currentDateString);

  window.pymChild.sendHeight();
}
